import Link from 'next/link';
import pluralize from 'pluralize';
import { memo, type ReactNode, useMemo } from 'react';

import { useTrackClickedRecentlyDeletedUpgradePrompt } from '@air/analytics';
import { Tooltip } from '@air/primitive-tooltip';

import { useDeletionRecoveryWindow } from '~/components/RecentlyDeleted/hooks/useDeletionRecoveryWindow';
import { PrivateRecentlyDeletedFilterButton } from '~/components/v2/components/recently-deleted/PrivateRecentlyDeletedFilterButton';
import { Routes } from '~/constants/routes';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { useCurrentWorkspaceMember } from '~/swr-hooks/members/useCurrentWorkspaceMember';
import { canManageSubscription } from '~/utils/permissions/workspacePermissions';

export const PrivateRecentlyDeletedHeader = memo(() => {
  const { currentWorkspaceMember } = useCurrentWorkspaceMember();
  const { recoveryWindow, configuredRecoveryWindow } = useDeletionRecoveryWindow();
  const { data: permissions } = useCurrentWorkspacePermissionsContext();
  const { trackClickedRecentlyDeletedUpgradePrompt } = useTrackClickedRecentlyDeletedUpgradePrompt();

  const canUpgrade = canManageSubscription(permissions);

  const description = useMemo<ReactNode>(() => {
    if (currentWorkspaceMember?.isSysAdmin) {
      return (
        <p className="max-w-[750px] pl-2 text-12 text-grey-10">
          This workspace is configured with {pluralize('day', configuredRecoveryWindow, true)} to recover items before
          they are permanently deleted. As a Sysadmin you have an extended {pluralize('day', recoveryWindow, true)} to
          recover items.
        </p>
      );
    }

    if (recoveryWindow) {
      return (
        <p className="max-w-[750px] pl-2 text-12 text-grey-10">
          You have {pluralize('day', recoveryWindow, true)} to recover items before they are permanently deleted.{' '}
          {canUpgrade ? (
            <Link
              className="font-semibold text-blue-10 hover:underline"
              href={Routes.workspaceAdmin.subscription}
              onClick={trackClickedRecentlyDeletedUpgradePrompt}
              target="_blank"
            >
              Upgrade your plan
            </Link>
          ) : (
            'Upgrade your plan'
          )}{' '}
          to extend this recovery window.
        </p>
      );
    }

    return null;
  }, [
    currentWorkspaceMember?.isSysAdmin,
    configuredRecoveryWindow,
    recoveryWindow,
    canUpgrade,
    trackClickedRecentlyDeletedUpgradePrompt,
  ]);

  return (
    <header className="flex items-end gap-3 border-b border-b-grey-4 px-4 pt-3 pb-4">
      <div className="flex grow flex-col">
        <h1 className="p-2 font-semibold text-24 text-grey-12">Recently deleted</h1>
        {description}
      </div>
      <div className="shrink-0 p-2">
        <Tooltip label="Settings">
          <PrivateRecentlyDeletedFilterButton />
        </Tooltip>
      </div>
    </header>
  );
});

PrivateRecentlyDeletedHeader.displayName = 'PrivateRecentlyDeletedHeader';
